import { Box, xcss } from '@atlaskit/primitives';
import React, { type ReactNode, useCallback, useRef } from 'react';

import { useSharedSubmenuState } from './hooks/useSubmenuState';

const shellStyles = xcss({
  paddingBlock: 'space.075',
});

/**
 * Wrapper for drilldown menu content.
 *
 * Provides consistent padding and closes any open submenu when the user
 * hovers over other interactive elements.
 */
const MenuContentShell = ({ children }: { children: ReactNode }) => {
  const submenuState = useSharedSubmenuState();
  const shellRef = useRef<HTMLDivElement>(null);

  const handleMouseOver = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      if (!submenuState || submenuState.activeSubmenuKey === null) {
        return;
      }
      const target = e.target as HTMLElement | null;
      if (!target) {
        return;
      }

      // Close any open submenu when the cursor moves to a different interactive item,
      // but ignore movement onto non-interactive areas, onto the open trigger itself,
      // or onto open submenus.
      if (!shellRef.current?.contains(target)) {
        return;
      }
      const menuItem = target.closest('button, [role="menuitem"]');
      if (!menuItem || menuItem.getAttribute('aria-expanded') === 'true') {
        return;
      }
      submenuState.closeAll();
    },
    [submenuState]
  );

  return (
    <Box xcss={shellStyles} testId="menu-content-shell">
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/mouse-events-have-key-events -- event delegation wrapper, not interactive */}
      <div ref={shellRef} onMouseOver={handleMouseOver}>
        {children}
      </div>
    </Box>
  );
};

export default MenuContentShell;
